import React from 'react'
import { OpaqueSparqlResults } from './sparqlResultHelpers'
import { Signal } from '@/utils/signals'
import { ComponentNamespace, useNamespace } from '@/redux/pubsub'
import {
  createUseSparqlQueryOptions,
  createUseConstructQueryOptions,
  ErrorNeedAuth,
  SparqlError,
} from './use-sparql-query'
import { queryClient } from '@/queryClient'

export { ErrorNeedAuth }

export type SparqlResultHolder = {
  result: OpaqueSparqlResults
  isLatest: () => boolean
  isError: boolean
}

const ERROR_RESULT = {
  head: {
    vars: []
  },
  results: {
    bindings: []
  }
} satisfies OpaqueSparqlResults

type QueryString = string

type TemplateId = string

type Status = "init" | "fetching" | "fetched"

/**
 * @deprecated compatibility shim for new code (developed after happy-configurator-ux) that uses SparqlManager
 */
export function useSparqlManager(sparqlEndpoint: string, templateId: TemplateId) {
  const namespace = useNamespace()
  const ref = React.useRef<SparqlManager>()
  if (!ref.current) ref.current = new SparqlManager(sparqlEndpoint, templateId, namespace) // TODO: sparqlEndpoint should change if it is changed
  return ref.current! // ! because ref.current is assigned above
}

/**
 * @deprecated compatibility shim for new code (developed after happy-configurator-ux) that uses SparqlManager
 * Co-authored-by: Claude
 */
export class SparqlManager {
  private readonly templateId: string
  private readonly sparqlEndpoint: string
  private readonly namespace: ComponentNamespace

  /** Counter so `isLatest()` can report whether a holder is stale */
  private requestSeq = 0

  public readonly dataSignal = new Signal<SparqlResultHolder>()
  public readonly statusSignal = new Signal<Status>()

  constructor(sparqlEndpoint: string, templateId: TemplateId, namespace: ComponentNamespace) {
    this.sparqlEndpoint = sparqlEndpoint
    this.templateId = templateId
    this.namespace = namespace
  }

  private setStatus = (status: Status) => {
    this.statusSignal.emit(status)
  }

  async fetch(query: QueryString, templateId?: string): Promise<SparqlResultHolder> {
    const seq = ++this.requestSeq
    const isLatest = () => seq === this.requestSeq

    this.setStatus('fetching')

    let isError = false
    let result: OpaqueSparqlResults = ERROR_RESULT
    try {
      result = await queryClient.fetchQuery(createUseSparqlQueryOptions({
        query,
        sparqlEndpoint: this.sparqlEndpoint,
        source: templateId ?? this.templateId,
        namespace: this.namespace,
      }))
    } catch (error) {
      if (error instanceof ErrorNeedAuth) throw error
      if (!(error instanceof SparqlError)) throw error
      isError = true
      result = ERROR_RESULT
    }

    const resultHolder: SparqlResultHolder = { isLatest, isError, result }
    this.setStatus('fetched')
    this.dataSignal.emit(resultHolder)
    return resultHolder
  }

  async fetchTurtle(query: QueryString, templateId?: string): Promise<string> {
    return queryClient.fetchQuery(createUseConstructQueryOptions({
      query,
      sparqlEndpoint: this.sparqlEndpoint,
      source: templateId ?? this.templateId,
      namespace: this.namespace,
    }))
  }
}
